/**
 * Hook per gestire le notifiche utente (promemoria, avvisi, ecc.)
 * Supporta realtime per aggiornamenti immediati
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useEffect } from "react";

export interface UserNotification {
  id: string;
  user_id: string;
  notification_type: string;
  category: string;
  title: string;
  message: string;
  action_url?: string;
  action_label?: string;
  related_object_type?: string;
  related_object_id?: string;
  is_read: boolean;
  read_at?: string;
  created_at: string;
  expires_at?: string;
}

export const useUserNotifications = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Query per il conteggio notifiche non lette
  const { data: unreadCount = 0 } = useQuery({
    queryKey: ["user-notifications-count", user?.id],
    queryFn: async () => {
      if (!user) return 0;
      const { count, error } = await supabase
        .from("user_notifications")
        .select("*", { count: "exact", head: true })
        .eq("user_id", user.id)
        .eq("is_read", false);
      if (error) throw error;
      return count || 0;
    },
    enabled: !!user,
    refetchInterval: 60000, // Refresh ogni minuto
  });

  // Query per le notifiche complete
  const { data: notifications = [], isLoading } = useQuery({
    queryKey: ["user-notifications", user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from("user_notifications")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return data as UserNotification[];
    },
    enabled: !!user,
  });

  // Mutation per segnare come letta
  const markAsReadMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      const { error } = await supabase
        .from("user_notifications")
        .update({ is_read: true, read_at: new Date().toISOString() })
        .eq("id", notificationId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-notifications", user?.id] });
      queryClient.invalidateQueries({ queryKey: ["user-notifications-count", user?.id] });
    },
  });

  // Mutation per segnare tutte come lette
  const markAllAsReadMutation = useMutation({
    mutationFn: async () => {
      if (!user) return;
      const { error } = await supabase
        .from("user_notifications")
        .update({ is_read: true, read_at: new Date().toISOString() })
        .eq("user_id", user.id)
        .eq("is_read", false);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-notifications", user?.id] });
      queryClient.invalidateQueries({ queryKey: ["user-notifications-count", user?.id] });
    },
  });

  // Realtime subscription per nuove notifiche
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel(`user-notifications-${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "user_notifications",
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ["user-notifications", user.id] });
          queryClient.invalidateQueries({ queryKey: ["user-notifications-count", user.id] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, queryClient]);

  return {
    notifications,
    unreadCount,
    isLoading,
    markAsRead: markAsReadMutation.mutate,
    markAllAsRead: markAllAsReadMutation.mutate,
    isMarkingAsRead: markAsReadMutation.isPending,
    isMarkingAllAsRead: markAllAsReadMutation.isPending,
  };
};
